import { ChangeDetectionStrategy, Component, computed, Input, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbActiveModal, NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { GenericSelectorTargets, InteractionType, UIContextAction } from 'angular-to-phaser';
import { Observable, defer, of } from 'rxjs';
import { ITakeServerMetadata, NestableAuthenticatedView } from 'src/app/components/base.component';
import { MainpageComponent } from 'src/app/components/mainpage/mainpage.component';
import { ChainedInteractionReply, DirectActionReply, firstText, ImageNode, Interaction, InteractionReply, Node, NodeType, RefreshTarget, RouteLinkReply, ServerComponentLinkReply } from 'src/app/dto';
import { ApplicationService } from 'src/app/services/application.service';
import { WorldService } from 'src/app/services/world.service';
import { GenericSelectorComponent } from '../../generic-selector/generic-selector.component';
import { GenericModalComponent } from '../generic-modal/generic-modal.component';

@Component({
    selector: 'app-interactor',
    templateUrl: './interactor.component.html',
    styleUrls: ['./interactor.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InteractorComponent extends NestableAuthenticatedView implements OnInit {

    private MODAL_OPTIONS_ROUTE: NgbModalOptions = { backdrop: 'static', scrollable: true, size: 'xl' };
    private MODAL_OPTIONS_INTERACTOR: NgbModalOptions = { backdrop: 'static', scrollable: false, size: 'lg' };

    public InteractionType = InteractionType;
    public NodeType = NodeType;
    public firstText = firstText;

    public activeModal: NgbActiveModal;
    @Input() injectedClass: string;
    protected modalTitle = computed(() => firstText(this.interaction()?.Content));


    @Input() injectedInteraction: Interaction;//these 3 are all the same!!!!!
    // equal: () => false because removeChildFromParent()/replaceChildForParent() splice this
    // interaction's own Quests/Responses in place (from a child interactor's call, so from outside
    // this component's template), and the .update(i => i) after each must reach the template.
    interaction = signal<Interaction | null>(null, { equal: () => false });

    @Input() parentInteraction: InteractorComponent;
    @Input() newChain: boolean = false;
    // Backed by a signal, not a plain field - beginInteraction()/handleReplyActions() below
    // reassign this internally, including from a path reachable via an async subscribe
    // (handleReplyActions' DirectActionReply branch) that isn't always paired with an
    // interaction.set() in the same synchronous tick. Read by this component's own template
    // ([breadcrumb]="this.breadcrumb", passed down to a nested <app-interactor>), so a plain
    // field write there would be invisible to OnPush. The getter/setter keeps every existing
    // this.breadcrumb read/write site working unchanged.
    private breadcrumbState = signal<string>(undefined);
    @Input() set breadcrumb(value: string) { this.breadcrumbState.set(value); }
    get breadcrumb(): string { return this.breadcrumbState(); }

    private disabledInteractions: Interaction[] = [];

    constructor(route: ActivatedRoute, router: Router, private coreAppService: ApplicationService,
        private worldService: WorldService, private modalService: NgbModal) {
        super(router, route, null, coreAppService.authService, MainpageComponent.route, coreAppService.uiStateService);
    }

    ngOnInit(): void {
        if (this.injectedInteraction) {
            this.beginInteraction(this.injectedInteraction, this.breadcrumb);
        }
    }

    public beginInteraction(interaction: Interaction, modalBreadcrumb: string) {
        this.breadcrumb = modalBreadcrumb;
        this.removeDisabledInteractions(interaction);

        this.interaction.set(interaction);
        this.scrollToInteractionView();
    }

    public sendReply(interactionReply: InteractionReply) {
        if (interactionReply.Actions && interactionReply.Actions.length) {
            this.handleReplyActions(interactionReply).subscribe();
        } else {
            if (interactionReply.UIContextAction == UIContextAction.Dismiss) { this.terminateCurrentInteraction(); }
            if (interactionReply.UIContextAction == UIContextAction.TerminateInteractionChain) { this.terminateInteractionChain(); }
        }
    }


    public terminateCurrentInteraction() {
        const current = this.interaction();
        if (current) {
            // Mutated in place, deliberately: a parent interactor's own Quests array holds this
            // exact object and renders off `newQuest.interactorDisabled`. Cloning to a new object
            // would both hide the flag from that other reference and strip Interaction's
            // prototype (removeFromNestedQuests et al).
            this.interaction.update(i => { i.interactorDisabled = true; return i; });
            if (this.parentInteraction) {
                this.parentInteraction.removeChildFromParent(current);
            }
        }
        this.interaction.set(null);
        if (this.activeModal) {
            this.activeModal.dismiss();
        }
    }

    private removeChildFromParent(child: Interaction) {
        if (this.interaction() && child) {
            this.disabledInteractions.push(child); //throw baby!
            if (!this.interaction().removeFromNestedQuests(child)) {
                this.interaction().removeFromNestedResponses(child);
            }
            this.interaction.update(i => i);
        }
    }

    private replaceChildForParent(child: Interaction, replacement: Interaction) {
        if (this.interaction() && child && replacement) {
            this.interaction().replaceInNestedQuests(child, replacement);
            this.interaction.update(i => i);
        }
    }

    private terminateCurrentInteractionAndChain() {
        this.terminateInteractionChain();
        this.terminateCurrentInteraction();
    }

    private terminateInteractionChain() {
        if (this.parentInteraction && !this.newChain) {
            this.parentInteraction.terminateCurrentInteractionAndChain();
        } else if (this.parentInteraction) {
            this.parentInteraction.removeChildFromParent(this.interaction());
        }
    }

    private replaceInteractionChain(replacement: Interaction) {
        if (this.parentInteraction && !this.newChain) {
            this.parentInteraction.replaceInteractionChain(replacement);
            this.terminateCurrentInteraction();
        }
        else {
            this.parentInteraction.replaceChildForParent(this.interaction(), replacement);
            this.beginInteraction(replacement, this.breadcrumb);
        }
    }



    // Contexts where the title is redundant (e.g. a nested quest accordion already shows its own
    // title in the accordion header) — matches the injectedClass values the removed
    // `.nested-quest-preview .interaction-title` / `.nested-modal-quest .interaction-title` CSS
    // rules used to target. Suppressed here instead of hidden with CSS so we don't need
    // ::ng-deep to reach into TitleNodeComponent's own view.
    private static readonly TITLE_HIDDEN_INJECTED_CLASSES = ['nested-quest-preview', 'nested-modal-quest'];

    public imageNode = computed(() => <ImageNode>this.interaction()?.Content?.find(n => n.NodeType === NodeType.Image));

    // Link-vs-button rendering derives from the reply's first action's ReplyActionType rather
    // than a separately-tracked DisplayMetadata.IsLink bool. Only RouteLinkReply (pure
    // client-side navigation, e.g. "visit our featured collector") renders as a plain anchor -
    // ServerComponentLinkReply (opens a routed modal, e.g. a gallery-shop picker or a minigame
    // launch) still renders as a normal button matching DisplayMetadata styling, same as
    // DirectActionReply. Treating both as links lost CornCob's "Impress Cornelius" and Igor's
    // "Test the Treat Machine" button styling.
    public isLinkReply(reply: InteractionReply): boolean {
        return reply.Actions?.[0] instanceof RouteLinkReply;
    }

    // Note: injectedClass is a plain @Input, not a signal, so this only re-derives when
    // interaction changes. That holds because injectedClass is written once per interactor
    // instance before its first render (a static template binding, or the assignment in
    // initializeInteractorModal) - making it vary over an instance's lifetime would require
    // converting it to a signal input too.
    public textAndTitleContent = computed<Node[]>(() => {
        const content = this.interaction()?.Content?.filter(n => n.NodeType !== NodeType.Image && n.NodeType !== NodeType.TaskProgress) ?? [];
        const injectedClasses = this.injectedClass?.split(' ') ?? [];
        const hideTitle = injectedClasses.some(c => InteractorComponent.TITLE_HIDDEN_INJECTED_CLASSES.includes(c));
        return hideTitle ? content.filter(n => n.NodeType !== NodeType.Title) : content;
    });

    public taskProgressNodes = computed<Node[]>(() => this.interaction()?.Content?.filter(n => n.NodeType === NodeType.TaskProgress) ?? []);

    private scrollToInteractionView(): void {
        setTimeout(() => {
            const actionBoxDiv = document.getElementById('interaction-view');
            if (actionBoxDiv) {
                const actionBox = actionBoxDiv.getBoundingClientRect();
                document.getElementById('main').scrollTo({ left: 0, top: actionBox.y - actionBox.height / 3, behavior: 'smooth' });
            }
        }, 200);
    }

    private handleReplyActions(interactionReply: InteractionReply): Observable<void> {
        return defer(() => {
            this.breadcrumb = this.breadcrumb ?? interactionReply.OptionText;
            for (let action of interactionReply.Actions) {

                if (action instanceof DirectActionReply) {
                    this.subscriptions.push(
                        this.worldService.submitAction(action.DirectAction.ExternalId, action.DirectAction.ActionCode, action.DirectAction.RecipientType, this.coreAppService.UserArgumentBag)
                            .subscribe(interaction => this.handleInteractionChange(interaction, interactionReply.UIContextAction)));
                } else if (action instanceof RouteLinkReply || action instanceof ServerComponentLinkReply) {
                    if (interactionReply.UIContextAction === UIContextAction.NewModalContext || interactionReply.UIContextAction === UIContextAction.ModalReplaceCurrentContext) {
                        this.openNewModal(action.NextEvent, null, interactionReply, action, interactionReply.OptionText);
                        if (interactionReply.UIContextAction === UIContextAction.ModalReplaceCurrentContext) {
                            this.terminateCurrentInteraction();
                        }
                    }
                    else {//(interactionReply.UIContextAction === UIContextAction.MaintainCurrentContext || interactionReply.UIContextAction === UIContextAction.Dismiss) {
                        this.router.navigate([action.Path]);
                    }
                } else if (action instanceof ChainedInteractionReply) {
                    this.breadcrumb = this.breadcrumb ?? interactionReply.OptionText
                    this.handleInteractionChange(action.NextEvent, interactionReply.UIContextAction);
                }
            }
            return of(void 0);
        });
    }

    private removeDisabledInteractions(newInteraction: Interaction) {
        if (this.interaction() == null || newInteraction == null) return;

        if (this.disabledInteractions && this.disabledInteractions.length > 0) {
            this.disabledInteractions.forEach(disabled => {
                if (newInteraction.removeFromNestedQuests(disabled)) {
                    if (!this.interaction().removeFromNestedQuests(disabled)) {
                        this.interaction().removeFromNestedResponses(disabled);
                    }
                }
            });
        }
    }

    private handleInteractionChange(newInteraction: Interaction, uiContext: UIContextAction) {
        switch (uiContext) {
            case UIContextAction.Dismiss: {
                this.terminateCurrentInteraction();
                break;
            }
            // @ts-expect-error
            case UIContextAction.MaintainCurrentContextTerminateChain: {
                this.terminateInteractionChain();
                //fallthrough to Maintain :)
            }
            case UIContextAction.MaintainCurrentContext: {
                this.beginInteraction(newInteraction, this.breadcrumb);
                break;
            }
            case UIContextAction.NewModalContext: {
                this.openNewModal(newInteraction, this.breadcrumb, null, null, this.modalTitle());
                break;
            }
            case UIContextAction.TerminateInteractionChain: {
                this.terminateCurrentInteractionAndChain();
                break;
            }
            case UIContextAction.ModalReplaceCurrentContext: {
                this.terminateInteractionChain();
                this.openNewModal(newInteraction, this.breadcrumb, null, null, this.modalTitle());
                break;
            }
            case UIContextAction.ReplaceCurrentInteractionChain: {
                this.replaceInteractionChain(newInteraction);
                break;
            }
            default: throw `Interactor missing mapping for UIContextAction ${uiContext}, meaning this interaction is busted`
        }
    }


    private openNewModal(interaction: Interaction, interactionBreadcrumbTitle: string, parentReply: InteractionReply, nestedAction: RouteLinkReply | ServerComponentLinkReply, modalTitle: string) {
        // Captured synchronously, before ModalReplaceCurrentContext's caller nulls
        // this.interaction (see handleReplyActions) - the closure below can't read
        // this.interaction itself, since by the time it actually runs (on modal close) that
        // field may have moved on to something else entirely.
        const refreshTarget = this.interaction()?.RefreshTarget;
        const questId = this.interaction()?.Id;

        if (interaction && !nestedAction) {
            this.coreAppService.uiStateService.openModalWithComponent(
                this.MODAL_OPTIONS_INTERACTOR,
                interactionBreadcrumbTitle,
                modalTitle,
                InteractorComponent,
                GenericModalComponent,
                this.initializeInteractorModal(interaction, interactionBreadcrumbTitle));

        } else if (nestedAction) {
            const serverComponentLink = nestedAction instanceof ServerComponentLinkReply ? nestedAction : null;
            this.MODAL_OPTIONS_ROUTE.fullscreen = true;
            this.coreAppService.uiStateService.openModalWithRoute(
                this.MODAL_OPTIONS_ROUTE,
                nestedAction.Path,
                this.breadcrumb ?? parentReply.OptionText,
                modalTitle ?? this.modalTitle(),
                GenericModalComponent,
                this.initializeRoutedComponentModal(serverComponentLink?.Title, serverComponentLink?.Message, serverComponentLink?.Target, () => true, serverComponentLink?.InjectedClass),
                // NextEvent chases through when present (unchanged - e.g. CornCob's "Impress
                // Cornelius"); otherwise this modal's own Step defaults to refreshing itself
                // once closed, so a reply with nothing to chase through (e.g. Igor's Treat
                // Machine launch) never leaves the parent conversation stale.
                interaction ? (() => this.beginInteraction(interaction, interactionBreadcrumbTitle)).bind(this) : this.buildRefreshOnClose(refreshTarget, interactionBreadcrumbTitle, questId),
                // See GenericModalComponent.navigateToRouteForModal's own comment - a
                // loadChildren-registered route's real leaf component (e.g. GameComponent) is
                // unreachable from the componentDataInitializerFunc above, so questId travels as
                // a query param instead. Harmless no-op for any route whose target doesn't read it.
                questId != null ? { questId } : null);
        }
    }

    // Shared by both stale-modal call sites (this one, and PhaserComponent.handlePhaserEvent for
    // the Treat Machine's reward screen) via WorldService.resolveRefreshTarget - see
    // Docs/CritterClient/Interaction-Content-Rendering.md's refresh-target section. continuationId
    // is the conversation the player was just looking at before the modal opened (captured at the
    // openNewModal call site, before it's nulled out) - threaded through so the server can tell
    // this automatic regreet apart from a fresh walk-up.
    private buildRefreshOnClose(target: RefreshTarget, breadcrumb: string, continuationId?: number): (() => void) | null {
        const refresh$ = this.worldService.resolveRefreshTarget(target, continuationId);
        if (!refresh$) return null;
        return () => this.subscribe(refresh$, interaction => this.beginInteraction(interaction, breadcrumb));
    }

    /**
     * Function that builds a function.
     * Returned function is an initializer func, when invoked it will invoke init methods for the nested component.
     * This function is for initializing an interactor inside of a GenericModal
     * Outer (builder) func arguments are captured by closure and used by returned builder at invocation time.
     * JS sucks dude
     */
    private initializeInteractorModal = (interaction: Interaction, interactionBreadcrumbTitle: string) => //func that returns a func to build a closure. JS sucks dude.
        (
            interactor: InteractorComponent,
            openedModal: NgbActiveModal,
            parentModal: GenericModalComponent
        ) => {
            if (this.interaction() != null) {
                interactor.parentInteraction = this;
            }
            interactor.beginInteraction(interaction, interactionBreadcrumbTitle);
            interactor.activeModal = openedModal;
            interactor.injectedClass = (interactor.parentInteraction ? 'nested-' : '') + 'modal-'
                + (interaction.InteractionType == InteractionType.Interaction ?
                    'interaction' : interaction.InteractionType == InteractionType.Quest ? 'quest' : '')
        };



    /**
     * Function that builds a function to initialize a Routed Component inside a GenericModal (routed components are passed by their /url/path, not a ComponentRef)
     * Returned function is an initializer func, when invoked it will invoke init methods for the nested component.
     * Outer (builder) func arguments are captured by closure and used by returned builder at invocation time.
     * This is currently built primarily to support GenericSelector components, but its not limited that way.
     * JS sucks dude
     */
    private initializeRoutedComponentModal = (title: string,
        message: string,
        genericSelectorTarget: GenericSelectorTargets,
        genericSelectorFunction: (item: any) => boolean,
        injectedClass: string) =>
        (
            routedComponent: any /** COMPONENT FROM A COMPONENTREF.INSTANCE */,
            openedModal: NgbActiveModal,
            parentModal: GenericModalComponent
        ) => {

            parentModal.setInjectedClass(injectedClass);
            if (routedComponent.initSelectorTarget) { //basically a type check :(
                (<GenericSelectorComponent>routedComponent).initSelectorTarget(genericSelectorTarget, genericSelectorFunction);
            }

            if (routedComponent.acceptServerInput) { //interface check
                (<ITakeServerMetadata>routedComponent).acceptServerInput(title, message, injectedClass);
            }

            if (routedComponent.initModalParentForNav) { //interface check
                (<GenericSelectorComponent>routedComponent).initModalParentForNav(<any>parentModal)
            }
        }
}
